import {
  AdminModel,
  DashboardContainerInterface,
  DatatableBuilderInterface,
  DatatableTypeInterface,
  PaginationRequest,
  PROVIDER_NAMES,
  textFiltrable,
} from '@ckmk/angular';
import { Injectable, inject } from '@angular/core';
import { SolutionModel } from '@app/src/app/modules/solution/classes/default/model/solution.model';
import {
  faCopy,
  faEye,
  faPenToSquare,
  faTrash,
  faTrashArrowUp,
} from '@fortawesome/free-solid-svg-icons';

@Injectable()
export class SolutionDatatableType implements DatatableTypeInterface {
  protected readonly container = inject<DashboardContainerInterface>(
    PROVIDER_NAMES.DASHBOARD_CONTAINER,
  );

  public constructor() {}

  public async configure(
    builder: DatatableBuilderInterface<AdminModel>,
  ): Promise<void> {
    const auth = this.container.authService.auth() as AdminModel;

    builder
      .setAuth(auth)
      .addIdentifyColumn()
      .addActiveColumn({
        editableOptions: {
          guard: {
            permissions: ['PERMISSION_UPDATE_SOLUTION'],
          },
        },
      })
      .add({
        type: 'file',
        name: 'cover',
        label: 'Cover',
        config: {
          editable: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_SOLUTION'],
            },
          },
        },
      })
      .add({
        type: 'file',
        name: 'picture',
        label: 'Picture',
        config: {
          editable: true,
          fixed: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_SOLUTION'],
            },
          },
        },
      })
      .add({
        type: 'translation',
        name: 'category',
        label: 'Category',
        config: {
          render: (data: SolutionModel): any => {
            return data.category?.title;
          },
        },
      })
      .add({
        type: 'translation',
        name: 'title',
        label: 'Title',
        config: {
          sortable: true,
          editable: true,
          fixed: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_SOLUTION'],
            },
          },
          filterable: textFiltrable({ translateAble: true }),
          inputs: {
            fieldConfig: {
              type: 'text',
            },
          },
        },
      })
      .add({
        type: 'text',
        name: 'slug',
        label: 'Slug',
        config: {
          sortable: true,
          editable: true,
          editableOptions: {
            guard: {
              permissions: ['PERMISSION_UPDATE_SOLUTION'],
            },
          },
          filterable: textFiltrable(),
        },
      })
      .addTimestampsColumns(auth.timezone)
      .addBlameColumns()
      .withPayload({
        filters: [],
        pagination: new PaginationRequest(),
        sorts: { createdAt: 'DESC' },
        includeCategory: true,
        includeCover: true,
        includePicture: true,
        includeSoftDeleted: true,
      })
      .withActions([
        {
          name: 'show',
          label: 'Show',
          icon: faEye,
          guard: {
            permissions: ['PERMISSION_READ_SOLUTION'],
          },
          activeColor: 'var(--color-warning)',
          path: (sc: SolutionModel): any => [
            this.container.path('/solutions/show'),
            sc.id,
          ],
          filter: (sc: SolutionModel) => !sc.deletedAt,
        },
        {
          name: 'update',
          label: 'Update',
          icon: faPenToSquare,
          guard: {
            permissions: ['PERMISSION_UPDATE_SOLUTION'],
          },
          activeColor: 'var(--color-success)',
          path: (sc: SolutionModel): any => [
            this.container.path('/solutions/update'),
            sc.id,
          ],
          filter: (sc: SolutionModel) => !sc.deletedAt,
        },
        {
          name: 'copy',
          label: 'Copy',
          icon: faCopy,
          guard: {
            permissions: ['PERMISSION_COPY_SOLUTION'],
          },
          activeColor: 'var(--color-primary)',
          path: (sc: SolutionModel): any => [
            this.container.path('/solutions/copy'),
            sc.id,
          ],
          filter: (sc: SolutionModel) => !sc.deletedAt,
        },
        {
          name: 'delete',
          label: 'Delete',
          icon: faTrash,
          guard: {
            permissions: ['PERMISSION_DELETE_SOLUTION'],
          },
          activeColor: 'var(--color-danger)',
          filter: (sc: SolutionModel) => !sc.deletedAt,
        },
        {
          name: 'restore',
          label: 'Restore',
          icon: faTrashArrowUp,
          guard: {
            roles: ['ROLE_DEV'],
          },
          activeColor: 'var(--color-danger)',
          filter: (sc: SolutionModel) => !!sc.deletedAt,
        },
      ])
      .saveToStorage();
  }
}
